import { useMemo } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useGuild } from 'app/frontend/base/config/context/guild'
import {
  useGuildCandidates,
  usePageInfo,
} from 'app/frontend/base/config/queries'
import { hasAdminAccessToAllTeams } from 'app/frontend/base/config/permissions'
import { displayErrorToast } from 'app/frontend/base/helpers'
import {
  addAllCandidatesToTeam,
  addGuildCandidate,
  addManualGuildCandidate,
  GuildCandidateInput,
  GuildCandidatesResponse,
  GuildCandidateUpdate,
  NewGuildCandidateInput,
  removeGuildCandidate,
  setGuildCandidateTeams,
  updateGuildCandidate,
} from 'app/frontend/base/api/guildCandidates'

export const useGuildId = () => {
  const { guild, guildReference } = useGuild()
  return guild?.id ?? guildReference?.id
}

export const useIsCandidateAdmin = () => {
  const { guild } = useGuild()
  return hasAdminAccessToAllTeams(guild, 'base')
}

export const useIsOwnCandidate = (candidate: wowaudit.GuildCandidate) => {
  const {
    data: { user },
  } = usePageInfo()
  return !!user && candidate.userId === user.id
}

export const useCandidates = () => useGuildCandidates(useGuildId())

const useCandidateMutation = <TVariables>(
  mutationFn: (
    guildId: number,
    variables: TVariables,
  ) => Promise<GuildCandidatesResponse>,
  onSuccess?: () => void,
) => {
  const guildId = useGuildId()
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  return useMutation({
    mutationFn: (variables: TVariables) =>
      mutationFn(guildId as number, variables),
    onSuccess: (data) => {
      queryClient.setQueryData(['guildCandidates', { guildId }], data)
      onSuccess?.()
    },
    onError: (error) => displayErrorToast(error, t),
  })
}

export const useAddCandidate = (onSuccess?: () => void) => {
  const queryClient = useQueryClient()

  return useCandidateMutation(
    (guildId, input: GuildCandidateInput) => addGuildCandidate(guildId, input),
    () => {
      queryClient.invalidateQueries({ queryKey: ['pageInfo'] })
      onSuccess?.()
    },
  )
}

export const useAddManualCandidate = (onSuccess?: () => void) =>
  useCandidateMutation(
    (guildId, input: NewGuildCandidateInput) =>
      addManualGuildCandidate(guildId, input),
    onSuccess,
  )

export const useUpdateCandidate = (onSuccess?: () => void) => {
  const queryClient = useQueryClient()

  return useCandidateMutation(
    (
      guildId,
      {
        characterId,
        ...attributes
      }: GuildCandidateUpdate & { characterId: number },
    ) => updateGuildCandidate(guildId, characterId, attributes),
    () => {
      queryClient.invalidateQueries({ queryKey: ['team'] })
      onSuccess?.()
    },
  )
}

export const useRemoveCandidate = () => {
  const queryClient = useQueryClient()

  return useCandidateMutation(
    (guildId, characterId: number) =>
      removeGuildCandidate(guildId, characterId),
    () => queryClient.invalidateQueries({ queryKey: ['team'] }),
  )
}

export const useSetCandidateTeams = () => {
  const queryClient = useQueryClient()

  return useCandidateMutation(
    (
      guildId,
      { characterId, teamIds }: { characterId: number; teamIds: number[] },
    ) => setGuildCandidateTeams(guildId, characterId, teamIds),
    () => queryClient.invalidateQueries({ queryKey: ['team'] }),
  )
}

export const useAddAllToTeam = (onSuccess?: () => void) => {
  const queryClient = useQueryClient()

  return useCandidateMutation(
    (guildId, teamId: number) => addAllCandidatesToTeam(guildId, teamId),
    () => {
      queryClient.invalidateQueries({ queryKey: ['team'] })
      onSuccess?.()
    },
  )
}

export const useActiveTeams = () => {
  const { guild } = useGuild()
  return useMemo(
    () =>
      (guild?.teamReferences ?? []).filter((team) => !team.markedForDeletionAt),
    [guild],
  )
}

export const useUserIdsWithMultipleCandidates = () => {
  const { data } = useCandidates()

  return useMemo(() => {
    const counts = (data?.candidates ?? []).reduce<Record<number, number>>(
      (acc, candidate) => {
        if (
          candidate.id !== null &&
          candidate.userId &&
          candidate.status !== 'rejected'
        ) {
          acc[candidate.userId] = (acc[candidate.userId] ?? 0) + 1
        }
        return acc
      },
      {},
    )

    return new Set(
      Object.entries(counts)
        .filter(([, count]) => count > 1)
        .map(([userId]) => Number(userId)),
    )
  }, [data])
}

export const useCandidatesByRole = (candidates: wowaudit.GuildCandidate[]) =>
  useMemo(
    () =>
      [...candidates]
        .sort(
          (a, b) =>
            a.characterReference.characterClass.localeCompare(
              b.characterReference.characterClass,
            ) ||
            a.characterReference.name.localeCompare(b.characterReference.name),
        )
        .reduce<Record<string, wowaudit.GuildCandidate[]>>((acc, candidate) => {
          acc[candidate.role] = [...(acc[candidate.role] ?? []), candidate]
          return acc
        }, {}),
    [candidates],
  )
